import { Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { UiKitModule } from "@app/ui-kit.module";
import {
  AddConfigToProductListModalComponent,
  AddConfigToProductListRow,
} from "@components/add-config-to-product-list-modal/add-config-to-product-list-modal.component";
import { ModalProductItem } from "@components/floorplan-item-attachments-modal/floorplan-item-details-modal.component";
import { OperateOnConfigModalComponent } from "@components/operate-on-config-modal/operate-on-config-modal.component";
import { ProductImageComponent } from "@components/product-image/product-image.component";
import { MonitoringCategory, MonitoringProductService } from "@domain/product/monitoring/monitoring-product.service";
import { UtilityProductService } from "@domain/product/monitoring/utility-product.service";
import { FgdsProductType } from "@domain/product/product";
import { ProductService } from "@domain/product/product.service";
import { AlarmDeviceConfiguration } from "@domain/project/configurations/alarm-device-configuration";
import { FlameDetectorConfiguration } from "@domain/project/configurations/flame-detector-configuration";
import { GasWarningCenterConfiguration } from "@domain/project/configurations/gas-warning-center-configuration";
import { CentralStationConfiguration } from "@domain/project/configurations/monitoring/central-station-configuration";
import { DisplayConfiguration } from "@domain/project/configurations/monitoring/display-configuration";
import { KvmConfiguration } from "@domain/project/configurations/monitoring/kvm-configuration";
import { PatientMonitorConfiguration } from "@domain/project/configurations/monitoring/patient-monitor-configuration";
import { TelemetryConfiguration } from "@domain/project/configurations/monitoring/telemetry-configuration";
import { UtilityConfiguration } from "@domain/project/configurations/monitoring/utility-configuration";
import { PlasticSignConfiguration } from "@domain/project/configurations/plastic-sign-configuration";
import { ProductConfiguration } from "@domain/project/configurations/product-configuration";
import { SignalElementConfiguration } from "@domain/project/configurations/signal-element-configuration";
import { TransmitterConfiguration } from "@domain/project/configurations/transmitter-configuration";
import { NavigationService } from "@domain/project/navigation.service";
import { ProjectService } from "@domain/project/project.service";
import { UserService } from "@domain/user/user.service";
import { BusinessScope } from "@lib/constants/business-scope";
import { ModalService } from "@odx/angular/components/modal";
import { ConfigurationDetailsModalService } from "@project/configuration-details-modal.service";
import { FloorplanService } from "@project/floorplanner/floorplan.service";
import { MonitoringProductItem } from "@project/monitoring-product-item";
import { ProductItem } from "@project/product-item";
import { ProjectStateService } from "@project/project-state.service";
import { ToastHelper } from "@utils/toast-helper.service";
import { combineLatest, forkJoin, map, Observable, of, switchMap } from "rxjs";
import { take } from "rxjs/operators";

@Component({
  selector: "app-configured-product",
  templateUrl: "./configured-product.component.html",
  styleUrl: "./configured-product.component.scss",
  imports: [UiKitModule, ProductImageComponent],
  standalone: true,
})
export class ConfiguredProductComponent<T extends ProductConfiguration> implements OnChanges, OnInit {
  private productService = inject(ProductService);
  private navigationService = inject(NavigationService);
  private floorplanService = inject(FloorplanService);
  private projectStateService = inject(ProjectStateService);
  private projectService = inject(ProjectService);
  private monitoringProductService = inject(MonitoringProductService);
  private utilityProductService = inject(UtilityProductService);
  private configurationDetailsModalService = inject(ConfigurationDetailsModalService);
  private userService = inject(UserService);
  private toastHelper = inject(ToastHelper);
  private readonly businessScope = toSignal(this.userService.businessScope$, { initialValue: BusinessScope.FGDS });

  @Input({ required: true }) productConfiguration!: T;
  @Input({ required: true }) numberOfFloorplanItems!: number;
  @Input({ required: true }) editPath!: string;
  @Input() imageUrl!: string;
  @Input() fallbackImageUrl!: string;
  @Input() attachments: ProductItem[] = [];
  @Input() subtitlePrefix?: string;
  @Input() substanceSubtitle?: string;
  @Output() add = new EventEmitter<void>();
  @Output() delete = new EventEmitter<void>();
  @Output() dragStart = new EventEmitter<DataTransfer>();

  protected localizedName$!: Observable<string>;
  protected subtitle$!: Observable<string>;
  protected isDiscontinued$!: Observable<boolean>;
  protected isIncompleteConfiguration$!: Observable<boolean>;
  protected get productImageId(): string | undefined {
    if (this.businessScope() === BusinessScope.FGDS) {
      return this.productConfiguration.mainProductId;
    }

    if (this.productConfiguration instanceof KvmConfiguration) {
      return this.productConfiguration.transmitterProductId;
    }

    return undefined;
  }

  protected readonly canEditProductList = toSignal(
    combineLatest([this.userService.user$, this.projectService.hasUserWritePermission$]).pipe(
      map(([user, hasWritePermission]) => !user.readOnlyAccess && hasWritePermission === true),
    ),
    { initialValue: false },
  );

  private readonly modalService = inject(ModalService);

  ngOnInit() {
    this.subtitle$ = this.resolveSubtitle();
    this.localizedName$ = this.resolveLocalizedName();
    this.isDiscontinued$ = this.projectStateService.isConfigDiscontinued(this.productConfiguration.id);
    this.isIncompleteConfiguration$ = this.projectStateService.isConfigIncomplete(this.productConfiguration.id);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["subtitlePrefix"]) {
      this.subtitle$ = this.resolveSubtitle();
    }
  }

  onEditConfig(event: any) {
    event.stopPropagation();
    this.modalService.open(OperateOnConfigModalComponent, { data: "edit" }).onClose$.subscribe((result) => {
      if (result !== "confirmed") {
        return;
      }

      this.navigationService.toDeviceConfiguration(
        this.editPath,
        this.floorplanService.selectedFloorplan.id,
        this.productConfiguration.id,
      );
    });
  }

  onDeleteConfig(event: any) {
    event.stopPropagation();
    this.modalService.open(OperateOnConfigModalComponent, { data: "delete" }).onClose$.subscribe((result) => {
      if (result !== "confirmed") {
        return;
      }
      this.delete.emit();
    });
  }

  onShowAttachments(event: any) {
    event.stopPropagation();
    this.configurationDetailsModalService.open(this.productConfiguration);
  }

  onAdd(event: any) {
    // prevent event to be emitted when clicked on odx-menu icon or odx-menu div
    // event.stopPropagation() can't be applied to the elements directly as they are part of an odx component
    if (
      event.target.localName === "odx-icon" ||
      event.target.localName === "odx-action-group" ||
      event.target.classList[0] === "odx-dropdown__inner"
    ) {
      return;
    }
    this.add.emit();
  }

  onAddToProductList(event: Event) {
    event.stopPropagation();
    if (!this.canEditProductList()) {
      return;
    }
    this.buildProductListRows().subscribe({
      next: (rows) => {
        this.modalService
          .open(AddConfigToProductListModalComponent, {
            id: "add-config-to-product-list-modal",
            data: {
              title: this.productConfiguration.name,
              rows,
              quantity: Math.max(this.productConfiguration.additionalProductListQuantity, 1),
            },
          })
          .onClose$.subscribe((quantity) => {
            if (typeof quantity !== "number") {
              return;
            }
            this.productConfiguration.setAdditionalProductListQuantity(quantity);
            this.toastHelper.success(
              $localize`:@@toast.productList.added.header:Zur Produktliste hinzugefügt`,
              $localize`:@@toast.productList.added.message:${this.productConfiguration.name} wurde zur Produktliste hinzugefügt.`,
            );
          });
      },
      error: () => {
        this.toastHelper.error(
          $localize`:@@toast.productList.addFailed.header:Hinzufügen fehlgeschlagen`,
          $localize`:@@toast.productList.addFailed.message:Das Produkt konnte nicht zur Produktliste hinzugefügt werden.`,
        );
      },
    });
  }

  private buildFgdsProductItemsForPreview(): { productItems: ModalProductItem[]; attachments: ModalProductItem[] } {
    if (this.productConfiguration instanceof AlarmDeviceConfiguration) {
      return {
        productItems: this.productConfiguration.productIds.map((id) => ProductItem.create(id)),
        attachments: this.attachments,
      };
    } else if (
      this.productConfiguration instanceof SignalElementConfiguration ||
      this.productConfiguration instanceof PlasticSignConfiguration
    ) {
      return { productItems: [ProductItem.create(this.productConfiguration.productId)], attachments: [] };
    } else if (this.productConfiguration instanceof GasWarningCenterConfiguration) {
      if (this.productConfiguration.regard3000Configuration) {
        const productItems = [
          ...this.productConfiguration.regard3000Configuration.baseUnits,
          ...(this.productConfiguration.regard3000Configuration.inputModules ?? []),
          ...(this.productConfiguration.regard3000Configuration.relayModules ?? []),
          ...(this.productConfiguration.regard3000Configuration.gatewayModules ?? []),
          ...(this.productConfiguration.regard3000Configuration.lidUnits ?? []),
        ];
        const slotCovers = this.productConfiguration.regard3000Configuration.slotCovers;
        if (slotCovers) {
          productItems.push(slotCovers);
        }
        return {
          productItems,
          attachments: this.productConfiguration.attachments,
        };
      }
      if (this.productConfiguration.regard7000PlusConfiguration) {
        return {
          productItems: [
            ...this.productConfiguration.regard7000PlusConfiguration.dockingStations,
            ...this.productConfiguration.regard7000PlusConfiguration.inputModules,
            ...this.productConfiguration.regard7000PlusConfiguration.relayModules,
            ...this.productConfiguration.regard7000PlusConfiguration.gatewayModules,
            ...this.productConfiguration.regard7000PlusConfiguration.terminalBlocks,
            ...this.productConfiguration.regard7000PlusConfiguration.dashboards,
          ],
          attachments: this.productConfiguration.attachments,
        };
      }
      return {
        productItems: [ProductItem.create(this.productConfiguration.productId)],
        attachments: this.productConfiguration.attachments,
      };
    } else if (this.productConfiguration instanceof TransmitterConfiguration) {
      return {
        productItems: [
          {
            ...ProductItem.create(this.productConfiguration.productId),
            detectionRange: this.productConfiguration.defaultDetectionRange,
          },
        ],
        attachments: this.productConfiguration.attachments,
      };
    } else if (this.productConfiguration instanceof FlameDetectorConfiguration) {
      return {
        productItems: [
          {
            ...ProductItem.create(this.productConfiguration.variantId ?? []),
          },
        ],
        attachments: this.productConfiguration.attachments,
      };
    }

    return { productItems: [], attachments: [] };
  }

  private buildProductListRows(): Observable<AddConfigToProductListRow[]> {
    if (this.businessScope() === BusinessScope.MONITORING) {
      return this.buildMonitoringProductListRows();
    }

    return this.buildFgdsProductListRows();
  }

  private buildFgdsProductListRows(): Observable<AddConfigToProductListRow[]> {
    const { productItems, attachments } = this.buildFgdsProductItemsForPreview();
    const modalRows = [...productItems, ...attachments];
    if (modalRows.length === 0) {
      return of([]);
    }

    return forkJoin(
      modalRows.map((item) =>
        this.productService.getProductNameById(item.productId).pipe(
          map((name) => ({
            quantity: item.quantity,
            name: name || item.productId,
            partNumber: item.productId,
          })),
        ),
      ),
    );
  }

  private buildMonitoringProductListRows(): Observable<AddConfigToProductListRow[]> {
    const category = this.resolveMonitoringCategory();
    if (!category) {
      return of([]);
    }

    const quantities = this.resolveMonitoringItemQuantities();
    const deviceIds = Array.from(quantities.keys());
    if (deviceIds.length === 0) {
      return of([]);
    }

    return this.monitoringProductService.init().pipe(
      switchMap(() =>
        this.monitoringProductService.loadDevicesFromDB(deviceIds, category, this.productService.getFilterCountry()),
      ),
      map((devices) =>
        devices.map((device) => ({
          quantity: quantities.get(device.matNr) ?? 1,
          name: device.name,
          partNumber: device.matNr,
        })),
      ),
    );
  }

  private resolveMonitoringCategory() {
    if (this.productConfiguration instanceof PatientMonitorConfiguration) return MonitoringCategory.PATIENT_MONITORS;
    if (this.productConfiguration instanceof CentralStationConfiguration) return MonitoringCategory.CENTRAL_STATIONS;
    if (this.productConfiguration instanceof TelemetryConfiguration) return MonitoringCategory.TELEMETRY;
    if (this.productConfiguration instanceof KvmConfiguration) return MonitoringCategory.KVMS;
    if (this.productConfiguration instanceof DisplayConfiguration) return MonitoringCategory.DISPLAYS;
    if (this.productConfiguration instanceof UtilityConfiguration) return MonitoringCategory.UTILITY;
    return undefined;
  }

  private resolveMonitoringItemQuantities() {
    if (this.productConfiguration instanceof PatientMonitorConfiguration) {
      return MonitoringProductItem.buildDeviceQuantityMap(this.productConfiguration.getOptions());
    }
    if (this.productConfiguration instanceof CentralStationConfiguration) {
      return MonitoringProductItem.buildDeviceQuantityMap(this.productConfiguration.getOptions());
    }
    if (this.productConfiguration instanceof TelemetryConfiguration) {
      return MonitoringProductItem.buildDeviceQuantityMap(this.productConfiguration.getOptions());
    }
    if (this.productConfiguration instanceof KvmConfiguration) {
      return MonitoringProductItem.buildDeviceQuantityMap(this.productConfiguration.getOptions());
    }
    if (this.productConfiguration instanceof DisplayConfiguration) {
      return MonitoringProductItem.buildDeviceQuantityMap(this.productConfiguration.getOptions());
    }
    if (this.productConfiguration instanceof UtilityConfiguration) {
      return MonitoringProductItem.buildDeviceQuantityMap(this.productConfiguration.getOptions());
    }
    return new Map<string, number>();
  }

  private resolveLocalizedName(): Observable<string> {
    if (this.productConfiguration instanceof UtilityConfiguration) {
      return this.utilityProductService.resolveName(this.productConfiguration, this.productService.getFilterCountry());
    }
    if (
      this.productConfiguration instanceof PatientMonitorConfiguration ||
      this.productConfiguration instanceof CentralStationConfiguration ||
      this.productConfiguration instanceof TelemetryConfiguration ||
      this.productConfiguration instanceof KvmConfiguration ||
      this.productConfiguration instanceof DisplayConfiguration
    ) {
      return of(this.productConfiguration.name);
    }
    if (this.productConfiguration instanceof AlarmDeviceConfiguration) {
      if (this.productConfiguration.productIds.length > 1) {
        return of(
          $localize`:@@alarmDeviceConfiguration.combination.name:Alarm-Kombi (${this.productConfiguration.productIds.join(", ")}:listOfAlarmDeviceIds:)`,
        );
      }
    }
    if (this.productConfiguration instanceof GasWarningCenterConfiguration) {
      if (this.productConfiguration.regard3000Configuration) {
        return this.resolveRegard3000DisplayName(this.productConfiguration);
      } else if (this.productConfiguration.regard7000PlusConfiguration) {
        return of($localize`:@@gasWarningCenterConfiguration.regard7000.displayName:REGARD 7000`);
      }
    }
    return this.productService
      .getProductNameById(this.productConfiguration.mainProductId)
      .pipe(map((name) => name || this.productConfiguration.name));
  }

  private resolveRegard3000DisplayName(configuration: GasWarningCenterConfiguration): Observable<string> {
    return this.productService.getProductById(configuration.productId).pipe(
      map((product) => {
        const productName = (product?.getLocalizedName("de-DE") || product?.name || configuration.name).toLocaleLowerCase();
        return productName.includes("plus")
          ? $localize`:@@gasWarningCenterConfiguration.regard3000Plus.displayName:REGARD 3000 Plus`
          : $localize`:@@gasWarningCenterConfiguration.regard3000Base.displayName:REGARD 3000`;
      }),
    );
  }

  private resolveSubtitle(): Observable<string> {
    let subtitle = "";
    if (this.subtitlePrefix) {
      subtitle = this.subtitlePrefix;
    }

    const isRegard3000Configuration =
      this.productConfiguration instanceof GasWarningCenterConfiguration && !!this.productConfiguration.regard3000Configuration;
    if (!this.attachments?.length && !isRegard3000Configuration) {
      return of(subtitle);
    }

    return this.productService
      .getProductNamesByIds(
        this.productConfiguration.configuredProductIds,
        isRegard3000Configuration ? undefined : FgdsProductType.ATTACHMENT,
      )
      .pipe(
        map((attachments) => {
          if (subtitle.length) {
            subtitle += ", ";
          }
          attachments.forEach((attachment: string) => (subtitle += `${attachment}, `));
          return subtitle?.slice(0, -2);
        }),
      );
  }

  onDragStart($event: DragEvent) {
    const dataTransfer: DataTransfer | null = $event.dataTransfer;
    if (!dataTransfer) return;
    this.isDiscontinued$.pipe(take(1)).subscribe((isDiscontinued) => {
      if (!isDiscontinued) {
        this.dragStart.emit(dataTransfer);
      }
    });
  }

  protected get placedProductCount() {
    return this.productConfiguration.countPlacedProducts();
  }
}
